// Three-point estimate: each task's optimistic / most likely / pessimistic value as a
// Beta-PERT (src/lib/pert.js), summed by simulation. Two additions to classic PERT:
// a widening factor for ranges that are too narrow (estimates overrun, docs/backlog-
// prioritization-research.md#71-estimates-are-skewed), and a shared overrun factor for
// tasks that run late together. Tests: model.test.js.

import { Rng } from "../lib/rng.js";
import { pertMoments, samplePert } from "../lib/pert.js";
import { cdfPoints, meanOf, quantilesOf, shareWhere } from "../lib/ranges.js";
import { Z90 } from "../lib/ranges.js";

export const DRAWS = 20000;
export const SEED = 20261012;
export const MAX_TASKS = 60;
export const UNITS = ["days", "hours", "weeks", "points"];

/** How much to widen each stated range, around its most likely value. */
export const WIDEN = [
  { id: "as-stated", factor: 1, label: "As stated" },
  { id: "wider", factor: 1.5, label: "Wider (×1.5)" },
  { id: "much-wider", factor: 2, label: "Much wider (×2)" },
];

/**
 * How strongly tasks run late together: the sd of a shared log multiplier with mean 1.
 * "Some" means the whole plan is within about ±25% of its independent total 90% of
 * the time.
 */
export const TOGETHER = [
  { id: "independent", sd: 0, label: "Independent" },
  { id: "some", sd: 0.15, label: "Some" },
  { id: "strong", sd: 0.3, label: "Strong" },
];

/**
 * Problems with one task's three numbers, in words; empty when usable.
 * @param {{ low: number, likely: number, high: number }} t
 * @returns {string[]}
 */
export function taskIssues({ low, likely, high }) {
  const issues = [];
  if (![low, likely, high].every(Number.isFinite)) issues.push("All three numbers are needed.");
  else {
    if (low < 0) issues.push("Optimistic is below 0.");
    if (!(low <= likely && likely <= high)) issues.push("Numbers must run optimistic ≤ most likely ≤ pessimistic.");
  }
  return issues;
}

/**
 * Widen a task's range around its most likely value, never below 0.
 * @param {{ low: number, likely: number, high: number }} t
 * @param {number} factor >= 1
 * @returns {{ low: number, likely: number, high: number }}
 */
export function widen({ low, likely, high }, factor) {
  return { low: Math.max(0, likely - factor * (likely - low)), likely, high: likely + factor * (high - likely) };
}

/**
 * Simulate the total of the usable tasks.
 * @param {{ tasks: {label: string, low: number, likely: number, high: number}[], widen: string, together: string, target: number | null }} input
 * @param {{ draws?: number, seed?: number }} [opts]
 * @returns {object} usable and excluded tasks, total quantiles, chances, the classic PERT figures and an S-curve
 */
export function analysePert(input, { draws = DRAWS, seed = SEED } = {}) {
  const factor = (WIDEN.find((w) => w.id === input.widen) ?? WIDEN[0]).factor;
  const shared = (TOGETHER.find((t) => t.id === input.together) ?? TOGETHER[0]).sd;
  const usable = [];
  const excluded = [];
  input.tasks.forEach((t, i) => {
    const issues = taskIssues(t);
    if (issues.length) excluded.push({ index: i, label: t.label, reasons: issues });
    else usable.push({ ...t, ...widen(t, factor) });
  });
  const sumLikely = input.tasks.reduce((s, t, i) => (excluded.some((e) => e.index === i) ? s : s + t.likely), 0);
  if (!usable.length) return { usable, excluded, empty: true };

  const rng = new Rng(seed);
  const totals = new Float64Array(draws);
  for (let d = 0; d < draws; d++) {
    let total = 0;
    for (const t of usable) total += samplePert(rng, t.low, t.likely, t.high);
    // Shared multiplier with mean 1: exp(N(−s²/2, s²)).
    totals[d] = shared > 0 ? total * Math.exp(shared * rng.normal() - (shared * shared) / 2) : total;
  }
  const [p5, p50, p85, p95] = quantilesOf(totals, [0.05, 0.5, 0.85, 0.95]);
  const moments = usable.map((t) => pertMoments(t.low, t.likely, t.high));
  const classicMean = moments.reduce((s, m) => s + m.mean, 0);
  const classicSd = Math.sqrt(moments.reduce((s, m) => s + m.sd * m.sd, 0));
  const target = Number.isFinite(input.target) && input.target > 0 ? input.target : null;
  return {
    usable,
    excluded,
    empty: false,
    mean: meanOf(totals),
    p5,
    p50,
    p85,
    p95,
    sumLikely,
    chanceWithinLikely: shareWhere(totals, (x) => x <= sumLikely),
    target,
    chanceWithinTarget: target == null ? null : shareWhere(totals, (x) => x <= target),
    // Classic PERT: sum of task means, normal approximation, independent tasks.
    classic: { mean: classicMean, sd: classicSd, p85: classicMean + 1.0364333894937898 * classicSd, p95: classicMean + Z90 * classicSd },
    curve: cdfPoints(totals, 120),
  };
}

/** A number for the page: whole above 20, one decimal below. */
export function fmt(x) {
  if (!Number.isFinite(x)) return "—";
  return Math.abs(x) >= 20 ? Math.round(x).toLocaleString("en-US") : (Math.round(x * 10) / 10).toLocaleString("en-US");
}

/** A share as a whole percentage. */
export function pct(p) {
  return Number.isFinite(p) ? `${Math.round(p * 100)}%` : "—";
}

/**
 * Plain sentences for a ticket, a channel or an agent's prompt; each states what it rests on.
 * @param {object} r analysePert result
 * @param {string} unit
 * @returns {string[]}
 */
export function summaryLines(r, unit) {
  if (r.empty) return [];
  const n = r.usable.length;
  const lines = [
    `${n} task${n === 1 ? "" : "s"}: 50% chance done within ${fmt(r.p50)} ${unit}, 85% within ${fmt(r.p85)}, 95% within ${fmt(r.p95)} (three-point estimates summed by simulation).`,
    `Adding the most likely values gives ${fmt(r.sumLikely)} ${unit}, which is enough only ${pct(r.chanceWithinLikely)} of the time.`,
  ];
  if (r.target != null) lines.push(`Chance of finishing within the target of ${fmt(r.target)} ${unit}: ${pct(r.chanceWithinTarget)}.`);
  lines.push("This assumes the task list is complete; work nobody listed is not in these numbers.");
  return lines;
}
